import React, { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { FormRenderProps, FieldProps } from 'react-final-form';
import { Box } from '@mui/material';

import { Button, Divider } from '@occc/ui-design-system';
import {
  TextField,
  KeyboardDatePicker,
  formatNullToEmpty,
  parseEmptyToNull,
  StatesSingleAutocomplete,
  UnitedState,
} from '@occc-app/design-system';
import { LocationMultipleSelectField } from '@occc-app/routes/Location/components/LocationSelect/LocationMultipleSelectField';
import { LocationFragment } from '@occc/graphql';
import { formatPhoneNumberInternational } from '@occc/utils-phone-number';

import FieldWarning from './FieldWarning';
import { maxAllowedDate } from './validation';

export type EditModalFormFields = {
  birthdate?: null | Date;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string;
  homeStores?: LocationFragment[];
  primaryAddress?: {
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: UnitedState | null;
    zipcode?: string | null;
  } | null;
};

export type EditModalFormProps = FormRenderProps<EditModalFormFields> & {
  onHide: () => void;
};

const NameFields = styled(Box)(({ theme }) => ({
  display: 'flex',
  '& > :first-of-type': {
    marginRight: theme.spacing(3),
  },
}));

const Buttons = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(2),
  '& > :first-of-type': {
    marginRight: theme.spacing(2),
  },
}));

const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const useTextFieldProps: () => Partial<FieldProps<any, any>> = () => {
  return useMemo(
    () => ({
      format: formatNullToEmpty,
      parse: parseEmptyToNull,
    }),
    [],
  );
};

const EditModalForm = ({
  onHide,
  handleSubmit,
  form,
  submitting,
  pristine,
}: EditModalFormProps) => {
  const disableSubmit = pristine || submitting;
  const textFieldProps = useTextFieldProps();
  const { dirtyFields } = form.getState();
  const phoneOrEmailModified = dirtyFields['phone'] || dirtyFields['email'];

  return (
    <Form onSubmit={handleSubmit}>
      {phoneOrEmailModified && <FieldWarning />}

      <NameFields>
        <TextField
          name='firstName'
          label='First Name'
          fieldProps={textFieldProps}
          margin='normal'
        />
        <TextField name='lastName' label='Last Name' fieldProps={textFieldProps} margin='normal' />
      </NameFields>
      <TextField
        name='email'
        label='Email'
        type='email'
        fieldProps={textFieldProps}
        margin='normal'
      />
      <TextField
        name='phone'
        label='Phone'
        type='tel'
        fieldProps={{
          parse: parseEmptyToNull,
          format: (value) => {
            return formatPhoneNumberInternational(formatNullToEmpty(value));
          },
        }}
        margin='normal'
        required
      />
      <Box mt={2} mb={1}>
        <KeyboardDatePicker name='birthdate' label='Birthdate' maxDate={maxAllowedDate} />
      </Box>

      <LocationMultipleSelectField name='homeStores' label='Home Store(s)' />

      <Divider />

      <TextField
        name='primaryAddress.address1'
        label='Address 1'
        fieldProps={textFieldProps}
        margin='normal'
      />
      <TextField
        name='primaryAddress.city'
        label='City'
        fieldProps={textFieldProps}
        margin='normal'
      />
      <StatesSingleAutocomplete
        name='primaryAddress.state'
        label='State'
        inputProps={{ margin: 'normal' }}
      />
      <TextField
        name='primaryAddress.zipcode'
        label='Zipcode'
        fieldProps={textFieldProps}
        margin='normal'
      />

      <Buttons>
        <Button variant='outlined' onClick={onHide}>
          CANCEL
        </Button>
        <Button variant='contained' color='primary' type='submit' disabled={disableSubmit}>
          SUBMIT
        </Button>
      </Buttons>
    </Form>
  );
};

export default EditModalForm;
